// ConfirmationPage — Booking confirmed step. No forward CTA.
// Animated checkmark draw. PNR generated on mount. Dark palette.
// TODO(booking-API-PR): replace PNR with real booking response from POST /bookings.

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingProgressBar } from '@/components/BookingProgressBar';
import { Button } from '@/components/primitives';
import { notYetWiredToast } from '@/components/primitives';
import { BookingProvider, useBooking } from '@/lib/booking-store';
import {
  generatePNR,
  MOCK_FLIGHT,
  AIRPORT_TAXES_PAISE,
  INSURANCE_PAISE,
  getSeatUpgradePaise,
  formatPaise,
} from '@/lib/booking-mock';

// ─── ANIMATED CHECKMARK ──────────────────────────────────────────────────────
function AnimatedCheck() {
  return (
    <div
      className="w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #FF5A3C 0%, #FF8A4A 100%)',
        boxShadow: '0 20px 60px rgba(255,90,60,0.35)',
        animation: 'fade-up 0.5s cubic-bezier(.2,.7,.2,1.1) both',
      }}
    >
      <svg
        width="44"
        height="44"
        viewBox="0 0 44 44"
        fill="none"
        aria-hidden="true"
        style={{ display: 'block' }}
      >
        <path
          d="M10 22L18 30L34 14"
          stroke="white"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 40,
            strokeDashoffset: 40,
            animation: 'checkDraw 0.55s 0.3s ease forwards',
          }}
        />
      </svg>
      <style>{`
        @keyframes checkDraw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

// ─── DETAIL ROW ───────────────────────────────────────────────────────────────
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-[7px] border-b border-white/[0.07]">
      <span
        className="text-[12px] font-semibold text-[#8C98B0]"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        {label}
      </span>
      <span
        className="text-[13px] font-medium text-[#F4F3F0]"
        style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── INNER ────────────────────────────────────────────────────────────────────
function ConfirmationInner() {
  const navigate            = useNavigate();
  const { state, dispatch } = useBooking();
  const f                   = MOCK_FLIGHT;
  const p                   = state.passengers[0] ?? { title: 'Mr', firstName: 'Alex', lastName: 'Chen' };

  // Generate PNR once on mount
  // TODO(booking-API-PR): replace with real PNR from booking response.
  const pnr = React.useMemo(() => {
    if (state.pnr) return state.pnr;
    const newPnr = generatePNR();
    dispatch({ type: 'SET_PNR', pnr: newPnr });
    return newPnr;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selRow           = state.selectedSeat ? parseInt(state.selectedSeat, 10) : null;
  const seatUpgradePaise = selRow ? getSeatUpgradePaise(selRow) : 0;
  const insurancePaise   = state.insurance ? INSURANCE_PAISE : 0;
  const totalPaise =
    state.selectedFare.pricePaise +
    AIRPORT_TAXES_PAISE +
    seatUpgradePaise +
    insurancePaise;

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <BookingProgressBar step="confirmation" />

      <div
        className="max-w-[900px] mx-auto animate-fade-up"
        style={{ padding: '60px clamp(16px, 4vw, 64px) 80px' }}
      >
        {/* ── Success hero ──────────────────────────────────────────────── */}
        <div className="text-center mb-14">
          <AnimatedCheck />

          <h1
            className="text-[56px] font-bold text-[#F4F3F0] leading-none tracking-tight mb-2.5"
            style={{
              fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif',
              letterSpacing: '-0.04em',
            }}
          >
            You're flying!
          </h1>
          <p
            className="text-[18px] text-[#8C98B0] mb-6"
            style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
          >
            Your booking is confirmed. Safe travels, {p.firstName}.
          </p>

          {/* PNR badge */}
          <div
            className="inline-flex items-center gap-3 px-6 py-3 rounded-[14px] bg-[#131C30] border border-white/[0.14]"
          >
            <span
              className="text-[12px] text-[#8C98B0] tracking-[1.5px] font-semibold"
              style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
            >
              BOOKING REF
            </span>
            <span
              className="text-[22px] font-bold text-[#FF5A3C] tracking-[3px]"
              style={{ fontFamily: 'monospace' }}
            >
              {pnr}
            </span>
          </div>
        </div>

        {/* ── Flight summary card ───────────────────────────────────────── */}
        <div className="bg-[#131C30] rounded-[24px] border border-white/[0.07] p-7 mb-5">
          <div
            className="text-[11px] font-semibold tracking-[1.5px] text-[#8C98B0] mb-5"
            style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
          >
            FLIGHT DETAILS
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 28 }}>
            {/* Left: times */}
            <div>
              {/* Airline */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-[10px] flex items-center justify-center text-white text-[12px] font-bold flex-shrink-0"
                  style={{ background: f.logoColor }}
                >
                  {f.code}
                </div>
                <div>
                  <div
                    className="text-[14px] font-bold text-[#F4F3F0]"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    {f.airline}
                  </div>
                  <div
                    className="text-[12px] text-[#8C98B0]"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    Flight {f.flightNo} · Economy {state.selectedFare.label}
                  </div>
                </div>
              </div>

              {/* Depart / arrive times */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                <div>
                  <div
                    className="text-[10px] font-semibold tracking-[1.5px] text-[#8C98B0] mb-1"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    DEPARTS
                  </div>
                  <div
                    className="text-[28px] font-bold text-[#F4F3F0] leading-none"
                    style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                  >
                    {f.depart}
                  </div>
                  <div
                    className="text-[13px] font-semibold text-[#F4F3F0] mt-1"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    {f.from} · {f.fromCity}
                  </div>
                  <div
                    className="text-[11px] text-[#8C98B0]"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    {f.date}
                  </div>
                </div>
                <div>
                  <div
                    className="text-[10px] font-semibold tracking-[1.5px] text-[#8C98B0] mb-1"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    ARRIVES
                  </div>
                  <div
                    className="text-[28px] font-bold text-[#F4F3F0] leading-none"
                    style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                  >
                    {f.arrive}
                  </div>
                  <div
                    className="text-[13px] font-semibold text-[#F4F3F0] mt-1"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    {f.to} · {f.toCity}
                  </div>
                  <div
                    className="text-[11px] text-[#8C98B0]"
                    style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                  >
                    {f.arriveDate}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: detail rows */}
            <div>
              <DetailRow
                label="Passenger"
                value={`${p.title} ${p.firstName} ${p.lastName} · Adult`}
              />
              <DetailRow
                label="Seat"
                value={state.selectedSeat ? `${state.selectedSeat} · Economy` : 'Auto-assigned'}
              />
              <DetailRow
                label="Baggage"
                value={`${state.selectedFare.bag} + ${state.selectedFare.cabin}`}
              />
              <DetailRow
                label="Fare"
                value={`${state.selectedFare.label} — ${state.selectedFare.cancel}`}
              />
              {state.insurance && (
                <DetailRow label="Insurance" value="Travel cover included" />
              )}
              <DetailRow label="Status" value="Confirmed ✓" />

              <div className="flex justify-between pt-3">
                <span
                  className="text-[13px] font-bold text-[#F4F3F0]"
                  style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
                >
                  Total paid
                </span>
                <span
                  className="text-[16px] font-bold text-[#FF5A3C]"
                  style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
                >
                  {formatPaise(totalPaise)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Email notice ──────────────────────────────────────────────── */}
        <div
          className="flex items-center gap-3.5 px-5 py-3.5 rounded-[14px] mb-7"
          style={{
            background: 'rgba(255,90,60,0.07)',
            border:     '1px solid rgba(255,90,60,0.2)',
          }}
        >
          <span className="text-[20px]">📧</span>
          <p
            className="text-[13px] text-[#F4F3F0]"
            style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}
          >
            Confirmation and e-ticket sent to{' '}
            <strong>{state.contactEmail}</strong>.
            Check-in opens 48h before departure.
          </p>
        </div>

        {/* ── Action buttons ────────────────────────────────────────────── */}
        <div className="flex gap-3">
          <Button
            variant="ghost"
            size="lg"
            className="flex-1 rounded-[14px]"
            onClick={() => notYetWiredToast('Download e-ticket')}
          >
            ⬇ Download e-ticket
          </Button>
          <Button
            variant="ghost"
            size="lg"
            className="flex-1 rounded-[14px]"
            onClick={() => notYetWiredToast('View on app')}
          >
            📱 View on app
          </Button>
          <Button
            variant="soft"
            size="lg"
            className="flex-1 rounded-[14px]"
            style={{ background: '#1B3A6B', color: '#fff', border: 'none' } as React.CSSProperties}
            onClick={() => notYetWiredToast('Hotel booking')}
          >
            🏨 Book a hotel in {f.toCity}
          </Button>
          <Button
            variant="flame"
            size="lg"
            className="flex-1 rounded-[14px] font-bold"
            onClick={() => navigate('/')}
          >
            Back to home →
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT ──────────────────────────────────────────────────────────────────
export function ConfirmationPage() {
  return (
    <BookingProvider>
      <ConfirmationInner />
    </BookingProvider>
  );
}
